import {
  Component,
  Inject,
  OnDestroy,
  OnInit
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DropdownListOption } from "../../../../../shared/components/search-dropdown/search-dropdown.component";
import { TaxReturnTypeSubTypeService } from "../../../../../shared/util/tax-return-type-subtype.service";
import {
  TaxReturnSubTypeService,
  TaxReturnSubType
} from "../../../../../../swaggerServices";
import { takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl
} from "@angular/forms";
import { TenantConfigService } from "../../../../../shared/services/tenant-config.service";
import { ConfigStateService } from "../../../../config/config-state.service";
import { environment } from "../../../../../environments/environment";
import { TranslocoService } from "@jsverse/transloco";

interface AddUpdateTaxReturnSubTypeDialogData {
  taxReturnTypeId: number;
  taxReturnSubType: TaxReturnSubType;
  selectedPriorSubTypeIds: number[];
}

@Component({
    selector: "app-add-update-tax-return-subtype",
    templateUrl: "./add-update-tax-return-subtype.component.html",
    styleUrls: ["./add-update-tax-return-subtype.component.scss"],
    standalone: false
})
export class AddUpdateTaxReturnSubTypeComponent implements OnInit, OnDestroy {
  taxReturnTypeId: number;
  taxReturnSubType: TaxReturnSubType;
  selectedPriorSubTypeIds: number[];
  taxReturnAlias: string;

  form: UntypedFormGroup;
  isCreate: boolean = false;
  priorSubTypeOptions: DropdownListOption[];
  isSubTypeReferenced: boolean = false;

  taxReturnSubTypeStatusList = [
    { id: 1, value: 0, name: "Active" },
    { id: 2, value: 99, name: "Inactive" }
  ];

  isProductionMode: boolean;

  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private taxReturnSubTypeService: TaxReturnSubTypeService,
    public config: ConfigStateService,
    private utilService: TaxReturnTypeSubTypeService,
    private translocoService: TranslocoService,
    private dialogRef: MatDialogRef<AddUpdateTaxReturnSubTypeComponent>,
    @Inject(MAT_DIALOG_DATA) data: AddUpdateTaxReturnSubTypeDialogData
  ) {
    this.taxReturnTypeId = data.taxReturnTypeId;
    this.taxReturnSubType = data.taxReturnSubType;
    this.selectedPriorSubTypeIds = data.selectedPriorSubTypeIds;
    this.isProductionMode = environment.production;
  }

  ngOnInit() {
    this.translocoService
      .selectTranslate("tax-return-alias")
      .subscribe(value => (this.taxReturnAlias = value));
    this.isCreate = !this.taxReturnSubType;
    this.initForm();
    let optionsObs;
    if (this.isCreate) {
      optionsObs = this.utilService.getTaxReturnSubTypesByType(
        this.taxReturnTypeId
      );
    } else {
      optionsObs = this.utilService.getTaxReturnSubTypesByType(
        this.taxReturnTypeId,
        this.taxReturnSubType.id
      );
      this.isSubTypeReferenced = this.taxReturnSubType.isReferenced;
    }
    optionsObs.subscribe((options: any) => {
      this.priorSubTypeOptions = options.filter(
        d =>
          !this.selectedPriorSubTypeIds.includes(d.id) ||
          (this.taxReturnSubType &&
            d.id === this.taxReturnSubType.priorSubTypeId)
      );
      this.priorSubTypeOptions.unshift({ id: null, value: null, name: " " });
    });
  }

  get taxReturnSubTypeName(): AbstractControl {
    return this.form.get("taxReturnSubTypeName");
  }

  get taxReturnSubTypeKey(): AbstractControl {
    return this.form.get("taxReturnSubTypeKey");
  }

  get taxReturnSubTypePrior(): AbstractControl {
    return this.form.get("taxReturnSubTypePrior");
  }

  get taxReturnSubTypeStatus(): AbstractControl {
    return this.form.get("taxReturnSubTypeStatus");
  }

  get taxReturnSubTypeStartingStatus(): AbstractControl {
    return this.form.get("taxReturnSubTypeStartingStatus");
  }

  get taxReturnSubTypeLinkDocumentTypeId(): AbstractControl {
    return this.form.get("taxReturnSubTypeLinkDocumentTypeId");
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  saveTaxReturnSubType() {
    if (this.isCreate) {
      const postData = {
        typeId: this.taxReturnTypeId,
        subType: this.taxReturnSubTypeName.value,
        priorSubTypeId: this.taxReturnSubTypePrior.value
          ? this.taxReturnSubTypePrior.value.id
          : this.taxReturnSubTypePrior.value,
        alias: this.taxReturnSubTypeKey.value,
        status: this.taxReturnSubTypeStatus.value
          ? this.taxReturnSubTypeStatus.value.value
          : this.taxReturnSubTypeStatus.value,
        startingStatus: this.taxReturnSubTypeStartingStatus.value,
        linkDocumentTypeId: this.taxReturnSubTypeLinkDocumentTypeId.value
      };
      this.taxReturnSubTypeService
        .addTaxReturnSubType(postData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          result => {
            this.dialogRef.close(true);
          },
          error => {
            this.dialogRef.close(false);
          }
        );
    } else {
      if (this.taxReturnSubTypePrior.value) {
        const hasLoop = this.utilService.doesSettingPriorSubTypeGeneratesLoops(
          this.taxReturnSubType,
          this.taxReturnSubTypePrior.value.id
        );
        if (hasLoop) {
          this.taxReturnSubTypePrior.setErrors({ hasLoop });
          return;
        }
      }
      const postData = {
        id: this.taxReturnSubType.id || null,
        typeId: this.taxReturnTypeId,
        subType: this.taxReturnSubTypeName.value,
        priorSubTypeId: this.taxReturnSubTypePrior.value
          ? this.taxReturnSubTypePrior.value.id
          : this.taxReturnSubTypePrior.value,
        alias: this.taxReturnSubTypeKey.value,
        status: this.taxReturnSubTypeStatus.value
          ? this.taxReturnSubTypeStatus.value.value
          : this.taxReturnSubTypeStatus.value,
        startingStatus: this.taxReturnSubTypeStartingStatus.value,
        linkDocumentTypeId: this.taxReturnSubTypeLinkDocumentTypeId.value
      };
      this.taxReturnSubTypeService
        .updateTaxReturnSubType(postData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          result => {
            this.dialogRef.close(true);
          },
          error => {
            this.dialogRef.close(false);
          }
        );
    }
  }

  onClose() {
    this.dialogRef.close(false);
  }

  formatter(option) {
    return option.label;
  }

  private initForm() {
    this.form = this.formBuilder.group({
      taxReturnSubTypeName: ["", Validators.required],
      taxReturnSubTypeKey: ["", Validators.required],
      taxReturnSubTypePrior: [""],
      taxReturnSubTypeStatus: ["", Validators.required],
      taxReturnSubTypeStartingStatus: [""],
      taxReturnSubTypeLinkDocumentTypeId: [""]
    });

    if (!this.isCreate) {
      this.taxReturnSubTypeName.setValue(this.taxReturnSubType.subType);
      this.taxReturnSubTypeKey.setValue(this.taxReturnSubType.alias);
      this.taxReturnSubTypePrior.setValue(this.taxReturnSubType.priorSubTypeId);
      this.taxReturnSubTypeStatus.setValue(
        this.taxReturnSubTypeStatusList.find(
          tst => tst.value === this.taxReturnSubType.status
        ).value
      );
      this.taxReturnSubTypeStartingStatus.setValue(
        this.taxReturnSubType.startingStatus
      );
      this.taxReturnSubTypeLinkDocumentTypeId.setValue(
        this.taxReturnSubType.linkDocumentTypeId
      );
    }
  }
}
